import React, { useMemo } from "react";
import { Text } from "@salestrak/ui";
import { EntityTag } from "#app/components/entity";
import type { EntityData } from "#app/lib/entity-visuals";
import type { ActivityChange } from "../activity-types";
import { pluralize } from "../activity-utils";
import { MAX_DISPLAY_COUNT } from "../activity-types";

interface AssigneeChunkProps {
  change: ActivityChange;
}

export function AssigneeChunk({ change }: AssigneeChunkProps) {
  const entities = useMemo<EntityData[]>(() => {
    const users = (change.users ?? []).map((user) => ({
      kind: "user" as const,
      id: user.userId,
      user,
    }));
    const teams = (change.teams ?? []).map((team) => ({
      kind: "team" as const,
      id: team.teamId,
      team,
    }));
    return [...users, ...teams];
  }, [change.users, change.teams]);

  // "added" / "removed" an assignee read more naturally as "assigned" /
  // "unassigned" — the verb already implies the assignee noun before the names.
  const verb =
    change.action === "added"
      ? "assigned"
      : change.action === "removed"
        ? "unassigned"
        : change.action;

  if (entities.length > 0 && entities.length <= MAX_DISPLAY_COUNT) {
    return (
      <Text size="sm">
        {verb}
        <EntityNames entities={entities} />
      </Text>
    );
  }

  if (entities.length > MAX_DISPLAY_COUNT) {
    return (
      <Text size="sm">
        {verb}{" "}
        <Text size="sm">
          {entities.length} {pluralize(change.target.label, { count: entities.length })}
        </Text>
      </Text>
    );
  }

  return null;
}

/** " A", " A and B", " A, B and C" — each name a chip, led by a space. */
export function EntityNames({ entities }: { entities: EntityData[] }) {
  return entities.map((entity, index) => (
    <Text key={`${entity.kind}-${entity.id}-${index}`} size="sm">
      {" "}
      <EntityTag entity={entity} display="label" weight="semibold" as="text" />
      {entities.length > 2 && index < entities.length - 1 ? "," : ""}
      {entities.length > 1 && index === entities.length - 2 ? " and" : ""}
    </Text>
  ));
}
